import { useCallback, useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  listPlanningCycleRows,
  createPlanningCycle,
  archivePlanningCycle,
  reactivatePlanningCycle,
  type PlanningCycleRow,
} from '../../api/planningCycleApi.js';

// The financial year has one spelling, and this is where it is set. Budget
// plans, work orders and rates all copy the cycle code from here, so a year
// typed anywhere else would be a second name for the same year.

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const CARD: React.CSSProperties = {
  padding: 16,
  borderRadius: 8,
  marginBottom: 16,
  border: '1px solid var(--color-border-default)',
  background: 'var(--color-bg-elevated)',
};

function period(c: PlanningCycleRow): string {
  return `${MONTHS[c.startMonth - 1]} ${c.startYear} – ${MONTHS[c.endMonth - 1]} ${c.endYear}`;
}

export function PlanningCyclePage(): ReactNode {
  const { token } = useAuth();
  const [rows, setRows] = useState<readonly PlanningCycleRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [cycleCode, setCycleCode] = useState('');
  const [cycleName, setCycleName] = useState('');
  const [description, setDescription] = useState('');
  const [startMonth, setStartMonth] = useState(4);
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(3);
  const [endYear, setEndYear] = useState(new Date().getFullYear() + 1);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const r = await listPlanningCycleRows(token);
      setRows(r.data ?? []);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const created = await createPlanningCycle(
        {
          cycleCode: cycleCode.trim(),
          cycleName: cycleName.trim(),
          description: description.trim() || undefined,
          startMonth,
          startYear,
          endMonth,
          endYear,
        },
        token,
      );
      setNotice(`${created.cycleCode} created.`);
      setCycleCode('');
      setCycleName('');
      setDescription('');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (c: PlanningCycleRow) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (c.recordStatusCode === 'ACTIVE') {
        await archivePlanningCycle(c.planningCycleId, token);
        setNotice(`${c.cycleCode} archived.`);
      } else {
        await reactivatePlanningCycle(c.planningCycleId, token);
        setNotice(`${c.cycleCode} reactivated.`);
      }
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="planning-cycles">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>Financial years</h1>
      <p style={{ marginTop: 0, marginBottom: 16, color: 'var(--color-text-muted)' }}>
        The financial years this tenant plans in. Budget plans, work orders and rate
        cards all take their year from this list, so the code entered here is the
        one that appears everywhere.
      </p>

      {error && (
        <div role="alert" data-testid="cycle-error" style={{ color: 'var(--color-danger, #d32f2f)', marginBottom: 12 }}>
          {error}
        </div>
      )}
      {notice && (
        <div role="status" data-testid="cycle-notice" style={{ marginBottom: 12 }}>
          {notice}
        </div>
      )}

      <form onSubmit={(e) => void submit(e)} style={CARD} aria-label="new-planning-cycle">
        <h2 style={{ fontSize: 15, marginTop: 0 }}>Add a financial year</h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label htmlFor="pc-code" style={{ fontSize: 12 }}>Code</label>
            <input
              id="pc-code"
              value={cycleCode}
              placeholder="FY2027-28"
              onChange={(e) => setCycleCode(e.target.value)}
              required
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label htmlFor="pc-name" style={{ fontSize: 12 }}>Name</label>
            <input
              id="pc-name"
              value={cycleName}
              placeholder="Financial Year 2027-28"
              onChange={(e) => setCycleName(e.target.value)}
              required
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label htmlFor="pc-start-month" style={{ fontSize: 12 }}>Starts</label>
            <div style={{ display: 'flex', gap: 4 }}>
              <select id="pc-start-month" value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <input
                aria-label="start year"
                type="number"
                min={1900}
                max={2999}
                value={startYear}
                onChange={(e) => setStartYear(Number(e.target.value))}
                style={{ width: 90 }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label htmlFor="pc-end-month" style={{ fontSize: 12 }}>Ends</label>
            <div style={{ display: 'flex', gap: 4 }}>
              <select id="pc-end-month" value={endMonth} onChange={(e) => setEndMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <input
                aria-label="end year"
                type="number"
                min={1900}
                max={2999}
                value={endYear}
                onChange={(e) => setEndYear(Number(e.target.value))}
                style={{ width: 90 }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 200 }}>
            <label htmlFor="pc-desc" style={{ fontSize: 12 }}>Description (optional)</label>
            <input id="pc-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <button type="submit" disabled={busy} data-testid="cycle-save">Add</button>
        </div>
      </form>

      {rows === null ? (
        <p data-testid="cycles-loading">Loading…</p>
      ) : rows.length === 0 ? (
        <p data-testid="cycles-empty">No financial year is set up yet.</p>
      ) : (
        <table data-testid="cycles-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Code</th>
              <th style={{ textAlign: 'left' }}>Name</th>
              <th style={{ textAlign: 'left' }}>Period</th>
              <th style={{ textAlign: 'left' }}>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.planningCycleId}>
                <td><code>{c.cycleCode}</code></td>
                <td>{c.cycleName}</td>
                <td>{period(c)}</td>
                <td>{c.recordStatusCode}</td>
                <td>
                  <button
                    type="button"
                    disabled={busy}
                    data-testid={`toggle-${c.cycleCode}`}
                    onClick={() => void toggle(c)}
                  >
                    {c.recordStatusCode === 'ACTIVE' ? 'Archive' : 'Reactivate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
